import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

// Importar imagen de ambiente de Bahía
import bahiaAmbiente from "@/LambrinAmbiente/Bahía.png";
import saoPauloAmbiente from "@/LambrinAmbiente/São Paulo.png";
import fortalezaAmbiente from "@/LambrinAmbiente/Fortaleza.png";
import brasiliaAmbiente from "@/LambrinAmbiente/Brasília.png";
import rioAmbiente from "@/LambrinAmbiente/Río.png";
import manaosAmbiente from "@/LambrinAmbiente/Manaos.png";
import recifeAmbiente from "@/LambrinAmbiente/Recife.png";
import salvadorAmbiente from "@/LambrinAmbiente/Salvador.png";

const ProductsSection = () => {
  const [activeTab, setActiveTab] = useState<'lambrines' | 'pisos' | 'maderas'>('lambrines');
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

  // Datos de los productos con imágenes de implementación
  const products = {
    lambrines: [
      { 
        name: "Bahía", 
        sku: "1001", 
        image: "/images/lambrines/interior/1001.jpg",
        hoverImage: bahiaAmbiente, // Imagen de ambiente de Bahía
        description: "Diseño clásico y elegante"
      },
      { 
        name: "São Paulo", 
        sku: "1002", 
        image: "/images/lambrines/interior/1002.jpg",
        hoverImage: saoPauloAmbiente,
        description: "Estilo moderno urbano"
      },
      { 
        name: "Fortaleza", 
        sku: "1003", 
        image: "/images/lambrines/interior/1003.jpg",
        hoverImage: fortalezaAmbiente,
        description: "Tonos cálidos naturales"
      },
      { 
        name: "Brasília", 
        sku: "1004", 
        image: "/images/lambrines/interior/1004.jpg",
        hoverImage: brasiliaAmbiente,
        description: "Acabado contemporáneo"
      },
      { 
        name: "Río", 
        sku: "1005", 
        image: "/images/lambrines/interior/1005.jpg",
        hoverImage: rioAmbiente,
        description: "Textura premium"
      },
      { 
        name: "Manaos", 
        sku: "1006", 
        image: "/images/lambrines/interior/1006.jpg",
        hoverImage: manaosAmbiente,
        description: "Diseño exclusivo"
      },
      { 
        name: "Recife", 
        sku: "1007", 
        image: "/images/lambrines/interior/1007.jpg",
        hoverImage: recifeAmbiente,
        description: "Estilo tropical"
      },
      { 
        name: "Salvador", 
        sku: "1008", 
        image: "/images/lambrines/interior/1008.jpg",
        hoverImage: salvadorAmbiente,
        description: "Acabado refinado"
      },
    ],
    pisos: [
      { 
        name: "SPC Clásico", 
        sku: "2001", 
        image: "/images/placeholder-product.jpg",
        hoverImage: "/images/pisos/implementations/2001-room.jpg",
        description: "Resistente al agua"
      },
      { 
        name: "Madera Natural", 
        sku: "2002", 
        image: "/images/placeholder-product.jpg",
        hoverImage: "/images/pisos/implementations/2002-room.jpg",
        description: "Aspecto auténtico"
      },
      { 
        name: "Mármol Elegante", 
        sku: "2003", 
        image: "/images/placeholder-product.jpg",
        hoverImage: "/images/pisos/implementations/2003-room.jpg",
        description: "Lujo atemporal"
      },
    ],
    maderas: [
      // Los productos de maderas se mostrarán cuando agregues las imágenes desde Lovable
      // Por ahora mostramos un placeholder
      { 
        name: "Próximamente", 
        sku: "3001", 
        image: "/images/placeholder-product.jpg",
        hoverImage: "/images/placeholder-product.jpg",
        description: "Catálogo completo en desarrollo"
      },
    ]
  };

  // Animaciones (sin Variants para evitar conflictos de tipos)

  return (
    <section className="py-20 bg-gradient-to-b from-white via-[#F7F4F0] to-white relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#C6A76D] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#C6A76D] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 bg-[#C6A76D]/10 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-[#C6A76D]" />
            <span className="text-sm font-medium text-[#C6A76D]">Catálogo Premium</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#2A2520] mb-4">
            Nuestros Productos
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Descubre nuestra colección exclusiva de lambrines y pisos de la más alta calidad
          </p>
        </motion.div>
        
        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex space-x-2 bg-white p-2 rounded-full shadow-lg border border-[#C6A76D]/20">
            <button
              onClick={() => setActiveTab('lambrines')}
              className={`px-8 py-3 rounded-full font-medium transition-all duration-300 ${
                activeTab === 'lambrines' 
                  ? 'bg-gradient-to-r from-[#C6A76D] to-[#e2c98a] text-black shadow-md scale-105' 
                  : 'text-gray-600 hover:text-[#C6A76D]'
              }`}
            >
              Lambrines
            </button>
            <button
              onClick={() => setActiveTab('pisos')}
              className={`px-8 py-3 rounded-full font-medium transition-all duration-300 ${
                activeTab === 'pisos' 
                  ? 'bg-gradient-to-r from-[#C6A76D] to-[#e2c98a] text-black shadow-md scale-105' 
                  : 'text-gray-600 hover:text-[#C6A76D]'
              }`}
            >
              Pisos
            </button>
            <button
              onClick={() => setActiveTab('maderas')}
              className={`px-8 py-3 rounded-full font-medium transition-all duration-300 ${
                activeTab === 'maderas' 
                  ? 'bg-gradient-to-r from-[#C6A76D] to-[#e2c98a] text-black shadow-md scale-105' 
                  : 'text-gray-600 hover:text-[#C6A76D]'
              }`}
            >
              Maderas
            </button>
          </div>
        </div>

        {/* Products Grid */}
        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-6 mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          {products[activeTab] && products[activeTab].length > 0 ? products[activeTab].map((product, index) => (
            <motion.div
              key={`${product.sku}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
              onMouseEnter={() => setHoveredProduct(product.sku)}
              onMouseLeave={() => setHoveredProduct(null)}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              {/* Image Container with Hover Effect */}
              <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                {/* Main Product Image */}
                <img
                  src={product.image}
                  alt={product.name}
                  className={`w-full h-full object-cover transition-all duration-700 ${
                    hoveredProduct === product.sku ? 'scale-110 opacity-0' : 'scale-100 opacity-100'
                  }`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/placeholder-product.jpg';
                  }}
                />
                
                {/* Hover Image (Implementation) */}
                <img
                  src={product.hoverImage}
                  alt={`${product.name} implementado`}
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                    hoveredProduct === product.sku ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
                  }`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />

                {/* Overlay on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent transition-opacity duration-300 ${
                  hoveredProduct === product.sku ? 'opacity-100' : 'opacity-0'
                }`}>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white text-sm font-medium">Vista en ambiente</p>
                  </div>
                </div>

                {/* Badge */}
                <div className="absolute top-3 right-3 bg-[#C6A76D] text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  Premium
                </div>
              </div>

              {/* Product Info */}
              <div className="p-4 bg-white">
                <h3 className="font-bold text-lg text-[#2A2520] mb-1 group-hover:text-[#C6A76D] transition-colors">
                  {product.name}
                </h3>
                <p className="text-sm text-gray-500 mb-2">{product.description}</p>
                <p className="text-xs text-gray-400">SKU: {product.sku}</p>
              </div>

              {/* Hover Border Effect */}
              <div className="absolute inset-0 border-2 border-[#C6A76D] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </motion.div>
          )) : (
            <div className="col-span-full py-16 px-4">
              <div className="relative max-w-4xl mx-auto group">
                {/* Background pattern */}
                <div className="absolute inset-0 bg-[url('/images/wood-texture-light.jpg')] bg-cover bg-center rounded-2xl opacity-5 group-hover:opacity-10 transition-opacity duration-500"></div>
                
                {/* Decorative elements */}
                <div className="absolute top-4 right-4 w-16 h-1 bg-[#C6A76D] transform rotate-45 opacity-70 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 w-12 h-1 bg-[#C6A76D] transform -rotate-45 opacity-70 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Content */}
                <div className="relative bg-white/80 backdrop-blur-sm p-12 rounded-2xl border border-white/50 shadow-sm hover:shadow-lg transition-all duration-300">
                  <div className="text-center">
                    <h3 className="text-2xl md:text-3xl font-serif font-light text-[#2A2520] mb-4 tracking-wide">
                      Descubre Todos Nuestros Diseños
                    </h3>
                    <div className="w-16 h-0.5 bg-[#C6A76D] mx-auto my-6"></div>
                    <p className="text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                      Esta es solo una muestra de nuestra amplia variedad. Explora nuestro catálogo completo para ver todos nuestros diseños exclusivos. ¿Necesitas más información? Nuestro equipo está listo para atenderte.
                    </p>
                    <a
                      href="https://catalogo-elsaman.vercel.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-transparent border-2 border-[#C6A76D] text-[#2A2520] hover:bg-[#C6A76D] hover:text-white font-medium tracking-wide rounded-none transition-all duration-300 group-hover:scale-105"
                    >
                      <span>Explorar Catálogo</span>
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          className="text-center mt-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="inline-flex flex-col items-center gap-6 max-w-3xl mx-auto">
            {/* Info Box */}
            <div className="bg-gradient-to-br from-[#C6A76D]/10 via-[#e2c98a]/5 to-transparent border-2 border-[#C6A76D]/30 rounded-2xl p-6 md:p-8 shadow-lg">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#C6A76D] to-[#e2c98a] rounded-full flex items-center justify-center shadow-md">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="text-xl md:text-2xl font-bold text-[#2A2520] mb-2">
                    ¿Te interesa algún diseño?
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Para realizar tu compra o solicitar una cotización personalizada, ponte en contacto con nosotros. 
                    <span className="font-bold text-[#C6A76D]"> Te responderemos en menos de 24 horas</span> con toda la información que necesites.
                  </p>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <button
                  onClick={() => {
                    const element = document.getElementById('contacto');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#C6A76D] to-[#e2c98a] text-black font-bold text-lg rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  Solicitar Cotización
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <a
                  href="https://catalogo-elsaman.vercel.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-white border-2 border-[#C6A76D] text-[#C6A76D] font-bold text-lg rounded-full shadow-lg hover:bg-[#C6A76D] hover:text-white hover:scale-105 transition-all duration-300"
                >
                  Ver Catálogo Completo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            <p className="text-sm text-gray-500">
              Más de 50 diseños exclusivos disponibles • Envío nacional • Instalación profesional
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsSection;